import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Bell,
  Briefcase,
  Camera,
  CaretRight,
  CarProfile,
  Clock,
  Copy,
  CreditCard,
  GlobeHemisphereWest,
  Headset,
  HouseLine,
  IdentificationCard,
  MapPin,
  Moon,
  Share,
  ShieldCheck,
  SignOut,
  SlidersHorizontal,
  SteeringWheel,
  Sun,
  Trophy,
  User,
  UsersThree,
} from "@phosphor-icons/react";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { registerPushSubscription } from "../../components/NotificationBridge";
import { playNotificationSound, unlockNotificationSound } from "../../lib/notificationSound";
import { getPreciseLocation } from "../../lib/preciseLocation";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import { Badge, Button, Card, StatusBadge } from "../../components/ui-kit";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import InstallAppPrompt from "../../components/InstallAppPrompt";

const PREFERENCE_OPTIONS = [
  "Chauffeur bien note",
  "Trajet silencieux",
  "Aide avec bagages",
  "Climatisation",
  "Recu automatique",
];

const ACCOUNT_GROUPS = [
  {
    title: "Compte",
    items: [
      { icon: User, title: "Profil", subtitle: "Nom, telephone, email et preferences", to: "/app/account/profile" },
      { icon: CreditCard, title: "Paiements", subtitle: "Cash, Mobile Money direct et historique", to: "/app/wallet" },
      { icon: CarProfile, title: "Rides", subtitle: "Historique, recus et details des courses", to: "/app/account/rides" },
      { icon: MapPin, title: "Saved places", subtitle: "Maison, travail et lieux frequents", to: "/app/account/saved-places" },
      { icon: SlidersHorizontal, title: "Settings", subtitle: "Theme, langue et notifications", to: "/app/account/settings" },
    ],
  },
  {
    title: "Famille et travail",
    items: [
      { icon: UsersThree, title: "Family profile", subtitle: "Manage your family's rides", to: "/app/account/family" },
      { icon: Briefcase, title: "Work profile", subtitle: "Organiser les courses professionnelles", to: "/app/account/work" },
      { icon: HouseLine, title: "Home", subtitle: "Retour a la reservation rapide", to: "/app/account/home" },
    ],
  },
  {
    title: "Securite et opportunites",
    items: [
      { icon: ShieldCheck, title: "Safety", subtitle: "SOS, PIN course et suivi GPS", to: "/app/account/safety" },
      { icon: IdentificationCard, title: "Verify identity", subtitle: "IA: extraction et verification de piece", to: "/app/verify-identity" },
      { icon: Headset, title: "Support", subtitle: "Aide 24/7 et reclamations", to: "/app/account/support" },
      { icon: SteeringWheel, title: "Earn with Nexora", subtitle: "Devenir chauffeur ou partenaire flotte", to: "/app/account/earn" },
    ],
  },
];

function SettingRow({ icon: Icon, title, subtitle, action, testId }) {
  return (
    <div className="profile-setting-row rounded-2xl bg-white/5 p-3" data-testid={testId}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="profile-setting-copy min-w-0">
        <div className="profile-line-clamp text-sm font-bold">{title}</div>
        {subtitle && <div className="profile-line-clamp text-[11px] text-muted-foreground">{subtitle}</div>}
      </div>
      <div className="profile-setting-action">
        {action || <CaretRight size={18} weight="bold" className="text-muted-foreground" />}
      </div>
    </div>
  );
}

function AccountOptionRow({ icon: Icon, title, subtitle, to }) {
  return (
    <Link
      to={to}
      className="flex min-h-[4.5rem] items-center gap-3 rounded-2xl bg-white/5 p-3 transition hover:bg-white/10"
      data-testid={`account-option-${title.toLowerCase().replaceAll(" ", "-")}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon size={20} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="profile-line-clamp text-sm font-bold">{title}</div>
        {subtitle && <div className="profile-line-clamp text-[11px] text-muted-foreground">{subtitle}</div>}
      </div>
      <CaretRight size={18} weight="bold" className="shrink-0 text-muted-foreground" />
    </Link>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  const { user, logout, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const photoInputRef = useRef(null);
  const [loyalty, setLoyalty] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [rides, setRides] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState("");
  const [savingCity, setSavingCity] = useState(false);
  const [pushState, setPushState] = useState(() => ({
    supported: "Notification" in window,
    permission: "Notification" in window ? Notification.permission : "unsupported",
    vapid: Boolean(process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY),
  }));
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("nexora-notification-sound") !== "off");
  const [prefs, setPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nexora-ride-prefs")) || ["Recu automatique"];
    } catch {
      return ["Recu automatique"];
    }
  });

  useEffect(() => {
    Promise.all([api.get("/loyalty/me"), api.get("/referrals/me"), api.get("/rides"), api.get("/cities")])
      .then(([loyaltyRes, referralRes, rideRes, cityRes]) => {
        setLoyalty(loyaltyRes.data);
        setReferrals(referralRes.data);
        setRides(rideRes.data);
        setCities(Array.isArray(cityRes.data) ? cityRes.data : []);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    setCityId(user?.city_id || "");
  }, [user?.city_id]);

  useEffect(() => {
    localStorage.setItem("nexora-ride-prefs", JSON.stringify(prefs));
  }, [prefs]);

  const copyCode = () => {
    if (!referrals?.code) return;
    navigator.clipboard.writeText(referrals.code);
    toast.success("Code copie");
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({
        title: "NEXORA-CAB",
        text: `Rejoins-moi sur NEXORA-CAB avec le code ${referrals.code} - 2 000 CDF offerts !`,
      });
    } else {
      copyCode();
    }
  };

  const togglePreference = (label) => {
    setPrefs((current) =>
      current.includes(label) ? current.filter((item) => item !== label) : [...current, label]
    );
  };

  const requestNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error("Notifications non disponibles sur cet appareil");
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      await unlockNotificationSound();
      await registerPushSubscription().catch(() => {});
      setPushState((current) => ({ ...current, permission }));
      toast.success("Notifications activees");
      playNotificationSound();
    } else {
      setPushState((current) => ({ ...current, permission }));
      toast.error("Notifications refusees");
    }
  };

  const toggleSound = async () => {
    const next = !soundEnabled;
    localStorage.setItem("nexora-notification-sound", next ? "on" : "off");
    setSoundEnabled(next);
    if (next) {
      await unlockNotificationSound();
      playNotificationSound();
    }
  };

  const uploadProfilePhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo de profil enregistree");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveCity = async () => {
    if (!cityId) {
      toast.error("Choisissez une ville");
      return;
    }
    setSavingCity(true);
    getPreciseLocation()
      .then(async (loc) => {
        try {
          await api.post("/profile/city", {
            city_id: cityId,
            lat: loc.lat,
            lng: loc.lng,
            accuracy_m: loc.accuracy_m,
          });
          await refresh();
          toast.success("Ville confirmee et enregistree");
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          setSavingCity(false);
        }
      })
      .catch(() => {
        setSavingCity(false);
        toast.error("Autorisez la position GPS pour confirmer la ville");
      });
  };

  const tier = loyalty?.status;
  const currentCity = cities.find((c) => c.id === user?.city_id);
  const tierGradients = {
    bronze: "from-amber-700 to-amber-500",
    silver: "from-slate-400 to-slate-200",
    gold: "from-yellow-500 to-yellow-300",
    platinum: "from-cyan-400 to-purple-400",
  };

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title={t("profile.title")} />

      <div className="space-y-4 px-4 pt-2">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-secondary text-xl font-bold text-primary-foreground"
              data-testid="profile-photo-button"
            >
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={user?.name || "Profil"} className="h-full w-full object-cover" />
              ) : (
                user?.name?.[0]
              )}
              <span className="absolute bottom-0 right-0 rounded-tl-lg bg-black/70 p-1 text-white">
                <Camera size={12} weight="fill" />
              </span>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => uploadProfilePhoto(e.target.files?.[0])}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate font-heading text-xl font-bold" data-testid="profile-name">
                {user?.name}
              </div>
              <div className="truncate font-mono text-xs text-muted-foreground">{user?.email}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">{user?.phone}</div>
            </div>
            <Badge tone="primary" className="shrink-0">{user?.role || "client"}</Badge>
          </div>
        </Card>

        <InstallAppPrompt compact />

        <Card className="overflow-hidden p-0">
          <div className="relative aspect-[16/8] bg-black sm:aspect-[16/7]">
            <img
              src="/nexora-cab.png"
              alt="Nexora Cab"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 to-transparent p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Nexora Cab</div>
              <div className="responsive-title font-heading text-lg font-bold">Votre trajet, notre priorite</div>
            </div>
          </div>
        </Card>

        <Card className="space-y-4 p-4">
          <div>
            <div className="font-heading text-lg font-bold">{t("profile.options_title")}</div>
            <div className="text-[11px] text-muted-foreground">
              {t("profile.options_subtitle")}
            </div>
          </div>
          {ACCOUNT_GROUPS.map((group) => (
            <details key={group.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
              <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {group.title}
                <CaretRight size={14} weight="bold" className="transition details-open:rotate-90" />
              </summary>
              <div className="mt-2 space-y-2">
                {group.items.map((item) => (
                  <AccountOptionRow key={item.title} {...item} />
                ))}
              </div>
            </details>
          ))}
        </Card>

        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-heading text-lg font-bold">
                {t("profile.settings")}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t("profile.settings_subtitle")}
              </div>
            </div>
            <SlidersHorizontal size={22} weight="duotone" className="text-primary" />
          </div>

          <details className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Langue et ville
              <CaretRight size={14} weight="bold" />
            </summary>
            <div className="mt-2 space-y-2">
              <SettingRow
                icon={GlobeHemisphereWest}
                title={t("profile.language")}
                subtitle={t("profile.language_subtitle")}
                action={<LanguageSwitcher full />}
                testId="profile-language"
              />
              <SettingRow
                icon={MapPin}
                title={t("profile.city_service")}
                subtitle={
                  currentCity
                    ? t("profile.city_confirmed", { city: currentCity.name })
                    : t("profile.city_choose_hint")
                }
                action={
                  <div className="flex w-full flex-col gap-2 sm:min-w-[280px]">
                    <select
                      value={cityId}
                      onChange={(e) => setCityId(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-foreground"
                      data-testid="profile-city-select"
                    >
                      <option value="" className="bg-background">{t("profile.choose_city")}</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id} className="bg-background">
                          {city.name} - {city.province}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={saveCity}
                      disabled={savingCity || !cityId || cityId === user?.city_id}
                      className="rounded-full bg-primary px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-primary-foreground disabled:opacity-40"
                      data-testid="profile-city-save"
                    >
                      {savingCity ? t("profile.verifying_gps") : t("profile.confirm_city")}
                    </button>
                  </div>
                }
                testId="profile-city"
              />
            </div>
          </details>

          <details className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Notifications et affichage
              <CaretRight size={14} weight="bold" />
            </summary>
            <div className="mt-2 space-y-2">
              <SettingRow
                icon={Bell}
                title={t("profile.push")}
                subtitle={
                  pushState.vapid
                    ? t("profile.push_state", { state: pushState.permission })
                    : t("profile.push_missing")
                }
                action={
                  <button
                    type="button"
                    onClick={requestNotifications}
                    disabled={!pushState.supported || !pushState.vapid}
                    className="rounded-full bg-primary px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-primary-foreground disabled:opacity-40"
                  >
                    {t("profile.activate")}
                  </button>
                }
                testId="profile-push"
              />
              <SettingRow
                icon={Bell}
                title={t("profile.notification_sound")}
                subtitle={soundEnabled ? t("profile.sound_on") : t("profile.sound_off")}
                action={
                  <button
                    type="button"
                    onClick={toggleSound}
                    className={`rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${
                      soundEnabled ? "bg-secondary text-secondary-foreground" : "bg-white/10 text-muted-foreground"
                    }`}
                    data-testid="profile-notification-sound"
                  >
                    {soundEnabled ? t("profile.active") : t("profile.muted")}
                  </button>
                }
                testId="profile-sound"
              />
              <SettingRow
                icon={theme === "light" ? Sun : Moon}
                title={t("profile.display_mode")}
                subtitle={theme === "light" ? t("profile.light_active") : t("profile.dark_active")}
                action={
                  <div className="grid w-full grid-cols-2 overflow-hidden rounded-full border border-white/10 bg-white/5 p-1 sm:w-auto">
                    {[
                      { key: "dark", label: t("profile.dark"), icon: Moon },
                      { key: "light", label: t("profile.light"), icon: Sun },
                    ].map((item) => {
                      const Icon = item.icon;
                      const active = theme === item.key;
                      return (
                        <button
                          key={item.key}
                          onClick={() => setTheme(item.key)}
                          className={`flex items-center justify-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase sm:px-2.5 ${
                            active ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                          }`}
                          data-testid={`theme-${item.key}`}
                        >
                          <Icon size={12} weight="bold" />
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                }
                testId="profile-theme"
              />
            </div>
          </details>

          <details className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
            <summary className="flex cursor-pointer list-none items-center justify-between rounded-xl px-2 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Preferences de course
              <CaretRight size={14} weight="bold" />
            </summary>
            <div className="mt-2 rounded-2xl bg-white/5 p-3">
              <div className="flex flex-wrap gap-2">
                {PREFERENCE_OPTIONS.map((label) => {
                  const active = prefs.includes(label);
                  return (
                    <button
                      key={label}
                      onClick={() => togglePreference(label)}
                      className={`rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-widest press-down ${
                        active
                          ? "border-secondary/40 bg-secondary/15 text-secondary"
                          : "border-white/10 bg-white/5 text-muted-foreground"
                      }`}
                      data-testid={`pref-${label.toLowerCase().replaceAll(" ", "-")}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </details>
        </Card>

        {tier && (
          <Card className="overflow-hidden p-0">
            <div className={`bg-gradient-to-r ${tierGradients[tier.tier] || tierGradients.bronze} px-5 py-4 text-background`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Statut</div>
                  <div className="font-heading text-2xl font-bold">{tier.tier_label}</div>
                </div>
                <Trophy size={32} weight="fill" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Points fidelite</div>
                  <div className="font-heading text-3xl font-bold">{tier.points}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total depense</div>
                  <div className="font-mono font-bold">{formatCDF(loyalty.total_spent || 0)}</div>
                </div>
              </div>
              {tier.next_tier && (
                <>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-secondary"
                      style={{ width: `${Math.min(100, (tier.points / (tier.points + tier.points_to_next)) * 100 || 0)}%` }}
                    />
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    +{tier.points_to_next} pts vers <span className="font-bold text-foreground">{tier.next_tier_label}</span>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {referrals && (
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <div className="rounded-xl bg-secondary/15 p-2 text-secondary">
                <UsersThree size={18} weight="duotone" />
              </div>
              <div>
                <div className="font-heading text-lg font-bold">Parrainage</div>
                <div className="text-[11px] text-muted-foreground">
                  Offre +2 000 CDF a toi et a ton filleul
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/10 px-3 py-2">
              <div className="min-w-0 flex-1 break-all font-mono text-lg font-bold text-primary" data-testid="referral-code">
                {referrals.code}
              </div>
              <button onClick={copyCode} className="rounded-lg bg-primary/15 p-2 text-primary press-down" data-testid="copy-code">
                <Copy size={16} weight="bold" />
              </button>
              <button onClick={share} className="rounded-lg bg-primary/15 p-2 text-primary press-down" data-testid="share-code">
                <Share size={16} weight="bold" />
              </button>
            </div>
          </Card>
        )}

        <Card className="p-0">
          <div className="flex items-center gap-2 border-b border-white/10 p-4">
            <Clock size={18} weight="bold" />
            <div className="font-heading text-lg font-bold">Historique</div>
          </div>
          <div className="divide-y divide-white/5">
            {rides.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">Aucune course</div>
            )}
            {rides.slice(0, 10).map((ride) => (
              <Link
                key={ride.id}
                to={`/app/ride/${ride.id}`}
                className="flex flex-wrap items-center justify-between gap-3 p-3 hover:bg-white/5"
                data-testid={`hist-${ride.id}`}
              >
                <div className="min-w-0">
                  <div className="truncate text-xs font-bold">
                    #{ride.id.slice(0, 6).toUpperCase()} - {ride.vehicle_type}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(ride.created_at).toLocaleString("fr-FR", { dateStyle: "short" })}
                  </div>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <StatusBadge status={ride.status} />
                  <span className="font-mono text-sm font-bold">{formatCDF(ride.price)}</span>
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Button variant="outline" className="w-full" onClick={logout} data-testid="profile-logout">
          <SignOut weight="bold" /> Se deconnecter
        </Button>
      </div>

      <BottomNav role="customer" />
    </div>
  );
}
